<script setup>
/* eslint-disable no-unused-vars */ // Las variables de <script setup> se usan en el template
import { computed, onMounted, reactive, ref, watch } from "vue"; // eslint-disable-line
import api from "@/servicios/apiSRP"; // eslint-disable-line
import swal from "sweetalert";
import GrafoRolesUsuario from "../components/GrafoRolesUsuario.vue"; // eslint-disable-line

const apiUsuarios = api.SRP.RequisicionPago.AdministracionUsuario;

/** Variables  */
const roles = ref([]);
const relaciones = ref([]);
const usuarios = ref([]);
const catalogos = reactive({ empresas: [], areas: [], sucursales: [], error: "" });
const cargando = ref(true);
const guardando = ref(false);

const correo = ref("");
const buscando = ref(false);
const resultado = ref(null);
const filtroRoles = ref("");

const colaboradorVacio = () => ({
  nombre: "",
  apellidoPaterno: "",
  apellidoMaterno: "",
  empresaId: "",
  areaId: "",
  sucursalId: "",
  puesto: "",
  extension: "",
  clave: ""
});
const formulario = reactive({ nombreUsuario: "", roles: [], colaborador: colaboradorVacio() });
const nombreEditado = ref(false);

const buscador = ref("");
const filtroPerfiles = ref([]);
const orden = reactive({ columna: "userName", asc: true });

/** Perfil de pantalla que asigna Login.vue según el prefijo del nombre del rol */
function PerfilFront(nombre) {
  if (!nombre) return null;
  if (nombre.startsWith("Solicita")) return "Solicitador";
  if (nombre.startsWith("Autoriza")) return "Autorizador área";
  if (nombre.startsWith("Verificador área")) return "Verificador área";
  if (nombre.startsWith("Mesa de Control")) return "Mesa de Control";
  if (nombre.startsWith("Planeación Financiera")) return "Planeación Financiera";
  if (nombre.startsWith("Verificador CXP")) return "Verificador CXP";
  if (nombre.startsWith("Director Adjunto de Tesorería")) return "Tesoreria";
  if (nombre.startsWith("Conta")) return "Autorizador CXP";
  if (nombre.startsWith("Auditoria")) return "Auditoria";
  return null;
}

/** Computadas  */
const rolPorId = computed(() => Object.fromEntries(roles.value.map((r) => [r.id, r])));
const NombreRol = (id) => (rolPorId.value[id] ? rolPorId.value[id].name : `Rol ${id}`);
const UsuariosRol = (id) => (rolPorId.value[id] ? rolPorId.value[id].usuarios : 0);

/** Estado de la búsqueda: srp (ya existe), authhir (solo en AuthHIR) o nuevo (no existe en ningún lado) */
const modo = computed(() => {
  if (!resultado.value) return null;
  if (resultado.value.usuarioSRP) return "srp";
  if (resultado.value.existeEnAuthHIR) return "authhir";
  return "nuevo";
});

const gruposRoles = computed(() => {
  const texto = filtroRoles.value.trim().toLowerCase();
  const activos = roles.value.filter(
    (r) => r.activo && (!texto || r.name.toLowerCase().includes(texto) || r.id.toString() == texto)
  );
  const grupos = [
    { clave: "sol", titulo: "Solicitantes", roles: [] },
    { clave: "ver", titulo: "Verificadores de área", roles: [] },
    { clave: "aut", titulo: "Autorizadores de área", roles: [] },
    { clave: "glo", titulo: "Flujo global", roles: [] },
    { clave: "otr", titulo: "Sin perfil en el front", roles: [] }
  ];
  activos.forEach((r) => {
    const p = PerfilFront(r.name);
    const g =
      p == "Solicitador" ? 0 : p == "Verificador área" ? 1 : p == "Autorizador área" ? 2 : p ? 3 : 4;
    grupos[g].roles.push(r);
  });
  return grupos.filter((g) => g.roles.length);
});

const perfilesSeleccion = computed(() => [
  ...new Set(formulario.roles.map((id) => PerfilFront(NombreRol(id))).filter(Boolean))
]);

/**
 * Revisa el flujo de autorización que tendrá la persona con los roles elegidos,
 * con la misma lógica que usan Login.vue y ObtenerAutorizadoresAsignados.
 */
const revisionFlujo = computed(() => {
  const tieneAutorizaArea = formulario.roles.some((id) => PerfilFront(NombreRol(id)) == "Autorizador área");
  return formulario.roles.map((id) => {
    const nombre = NombreRol(id);
    const perfil = PerfilFront(nombre);
    const salidas = relaciones.value.filter((r) => r.rolId == id);
    const entradas = relaciones.value.filter((r) => r.autorizadorId == id);
    const lineas = [];
    const avisos = [];

    salidas.forEach((r) =>
      lineas.push({
        texto: `${r.esVerificador ? "Envía a verificación con" : "Envía a autorización con"} ${NombreRol(r.autorizadorId)}`,
        detalle: `${UsuariosRol(r.autorizadorId)} persona(s)`,
        alerta: UsuariosRol(r.autorizadorId) == 0
      })
    );
    entradas.forEach((r) =>
      lineas.push({
        texto: `${r.esVerificador ? "Verifica lo que envía" : "Autoriza lo que envía"} ${NombreRol(r.rolId)}`,
        detalle: `${UsuariosRol(r.rolId)} persona(s)`,
        alerta: false
      })
    );

    if (perfil == "Solicitador" && salidas.length == 0)
      avisos.push("No tiene destino en RolAutorizador: no podrá enviar requisiciones.");
    if (salidas.some((r) => UsuariosRol(r.autorizadorId) == 0))
      avisos.push("Alguno de sus destinos no tiene personas asignadas.");
    if (perfil == "Verificador área" && salidas.length == 0)
      avisos.push("No tiene autorizador configurado: al verificar no le aparecerá a quién enviar.");
    if (entradas.some((r) => !r.esVerificador) && perfil != "Autorizador área" && !tieneAutorizaArea)
      avisos.push(
        `Recibe requisiciones para autorizar, pero entra al front como «${perfil || "ningún perfil"}». Agrega un rol «Autoriza…» para que vea su bandeja.`
      );
    if (perfil == "Autorizador área" && entradas.length == 0)
      avisos.push("Nadie le envía requisiciones con este rol.");
    if (!perfil && entradas.length == 0 && salidas.length == 0)
      avisos.push("Este rol no da acceso a ninguna bandeja de SRP.");

    return { id, nombre, perfil, lineas, avisos };
  });
});

const puedeGuardar = computed(() => {
  if (guardando.value || !modo.value) return false;
  if (modo.value == "srp") return true;
  if (!formulario.nombreUsuario.trim() || formulario.roles.length == 0) return false;
  if (modo.value == "nuevo") {
    const c = formulario.colaborador;
    return !!(
      c.nombre.trim() &&
      c.apellidoPaterno.trim() &&
      c.apellidoMaterno.trim() &&
      c.clave.trim() &&
      c.puesto.trim() &&
      c.empresaId &&
      c.areaId &&
      c.sucursalId
    );
  }
  return true;
});

/** Comparación antes / después de los roles de la persona */
const rolesAntes = computed(() =>
  resultado.value && resultado.value.usuarioSRP ? Array.from(resultado.value.usuarioSRP.roles) : []
);
const cambios = computed(() => {
  const antes = new Set(rolesAntes.value);
  const despues = new Set(formulario.roles);
  const perfilesDe = (ids) => new Set(ids.map((id) => PerfilFront(NombreRol(id))).filter(Boolean));
  const pAntes = perfilesDe(rolesAntes.value);
  const pDespues = perfilesDe(formulario.roles);
  return {
    agregados: formulario.roles.filter((id) => !antes.has(id)),
    quitados: rolesAntes.value.filter((id) => !despues.has(id)),
    perfilesGana: [...pDespues].filter((p) => !pAntes.has(p)),
    perfilesPierde: [...pAntes].filter((p) => !pDespues.has(p)),
    sinCambios: rolesAntes.value.length == formulario.roles.length && formulario.roles.every((id) => antes.has(id))
  };
});

const areasFiltradas = computed(() =>
  catalogos.areas.filter((a) => !formulario.colaborador.empresaId || a.empresaId == formulario.colaborador.empresaId)
);

const usuariosFiltrados = computed(() => {
  const texto = buscador.value.trim().toLowerCase();
  const lista = usuarios.value.filter((u) => {
    const perfiles = PerfilesUsuario(u);
    const coincide =
      !texto ||
      (u.userName || "").toLowerCase().includes(texto) ||
      (u.email || "").toLowerCase().includes(texto) ||
      u.roles.some((r) => NombreRol(r).toLowerCase().includes(texto));
    const perfil =
      filtroPerfiles.value.length == 0 ||
      filtroPerfiles.value.some((p) => (p == "Sin perfil" ? perfiles.length == 0 : perfiles.includes(p)));
    return coincide && perfil;
  });
  const copia = lista.slice();
  copia.sort((a, b) => {
    const x = (a[orden.columna] || "").toString().toLowerCase();
    const y = (b[orden.columna] || "").toString().toLowerCase();
    return (x === y ? 0 : x > y ? 1 : -1) * (orden.asc ? 1 : -1);
  });
  return copia;
});

const perfilesDisponibles = [
  "Solicitador",
  "Verificador área",
  "Autorizador área",
  "Mesa de Control",
  "Planeación Financiera",
  "Verificador CXP",
  "Autorizador CXP",
  "Tesoreria",
  "Sin perfil"
];

/** Métodos  */
function PerfilesUsuario(u) {
  return [...new Set(u.roles.map((r) => PerfilFront(NombreRol(r))).filter(Boolean))];
}

/** "JUAN PEREZ" -> "Juan Perez" para el nombre visible en SRP */
function NombrePropio(texto) {
  return (texto || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function MensajeError(error) {
  const mensaje = error && error.response && error.response.data && error.response.data.mensaje;
  return mensaje || `${error}`;
}

function OrdenarPor(columna) {
  orden.asc = orden.columna === columna ? !orden.asc : true;
  orden.columna = columna;
}

function OrdenarClass(columna) {
  return orden.columna === columna ? `sorted active ${orden.asc ? "asc" : "desc"}` : "sorted desc";
}

function ClaseChip(id) {
  const p = PerfilFront(NombreRol(id));
  return p == "Solicitador" ? "s" : p == "Verificador área" ? "v" : p == "Autorizador área" ? "a" : "";
}

async function CargarDatos() {
  cargando.value = true;
  try {
    const [respFlujo, respUsuarios] = await Promise.all([
      apiUsuarios.ObtenerFlujoAutorizacion(),
      apiUsuarios.ListarUsuarios()
    ]);
    roles.value = Array.from(respFlujo.data.data.roles);
    relaciones.value = Array.from(respFlujo.data.data.relaciones);
    usuarios.value = Array.from(respUsuarios.data.data).map((u) => ({ ...u, roles: Array.from(u.roles) }));
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    cargando.value = false;
  }
}

async function CargarCatalogosAuthHIR() {
  try {
    const { data } = await apiUsuarios.ObtenerCatalogosAuthHIR();
    if (data.estado != "OK") {
      catalogos.error = data.mensaje;
      return;
    }
    catalogos.empresas = Array.from(data.data.empresas);
    catalogos.areas = Array.from(data.data.areas);
    catalogos.sucursales = Array.from(data.data.sucursales);
    catalogos.error = "";
  } catch (error) {
    catalogos.error = MensajeError(error);
  }
}

async function Buscar() {
  const email = correo.value.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    swal("Atención", "Escribe un correo válido, por ejemplo nombre@hircasa.com.mx.", "warning");
    return;
  }
  buscando.value = true;
  try {
    const { data } = await apiUsuarios.BuscarUsuario(email);
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, data.estado == "ERROR" ? "error" : "warning");
      return;
    }
    resultado.value = data.data;
    formulario.colaborador = colaboradorVacio();
    nombreEditado.value = false;
    if (data.data.usuarioSRP) {
      formulario.nombreUsuario = data.data.usuarioSRP.userName;
      formulario.roles = Array.from(data.data.usuarioSRP.roles);
    } else if (data.data.colaborador) {
      const c = data.data.colaborador;
      formulario.nombreUsuario = NombrePropio(
        [c.nombre, c.apellidoPaterno].filter(Boolean).join(" ").trim() || c.nombreCompleto || ""
      );
      formulario.roles = [];
    } else {
      formulario.nombreUsuario = "";
      formulario.roles = [];
      if (!catalogos.empresas.length) await CargarCatalogosAuthHIR();
    }
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    buscando.value = false;
  }
}

function Limpiar() {
  resultado.value = null;
  correo.value = "";
  formulario.nombreUsuario = "";
  formulario.roles = [];
  formulario.colaborador = colaboradorVacio();
  filtroRoles.value = "";
}

async function Guardar() {
  if (!puedeGuardar.value) return;
  guardando.value = true;
  try {
    let respuesta;
    if (modo.value == "srp") {
      respuesta = await apiUsuarios.ActualizarRolesUsuario({
        usuarioId: resultado.value.usuarioSRP.id,
        roles: formulario.roles
      });
    } else {
      const c = formulario.colaborador;
      respuesta = await apiUsuarios.GuardarUsuario({
        email: resultado.value.email,
        nombreUsuario: formulario.nombreUsuario.trim(),
        roles: formulario.roles,
        colaborador:
          modo.value == "nuevo"
            ? {
                nombre: c.nombre.trim(),
                apellidoPaterno: c.apellidoPaterno.trim(),
                apellidoMaterno: c.apellidoMaterno.trim(),
                empresaId: Number(c.empresaId),
                areaId: Number(c.areaId),
                sucursalId: Number(c.sucursalId),
                puesto: c.puesto.trim(),
                extension: c.extension.trim() || undefined,
                clave: c.clave.trim()
              }
            : undefined
      });
    }
    const { data } = respuesta;
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, data.estado == "ERROR" ? "error" : "warning");
      return;
    }
    swal("¡Excelente!", data.mensaje, data.data && data.data.creadoEnAuthHIR ? "info" : "success");
    await CargarDatos();
    await Buscar();
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    guardando.value = false;
  }
}

async function EditarUsuario(u) {
  correo.value = u.email;
  await Buscar();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function QuitarRol(id) {
  formulario.roles = formulario.roles.filter((r) => r != id);
}

/** El nombre visible se arma solo mientras no lo edite quien captura */
watch(
  () => [formulario.colaborador.nombre, formulario.colaborador.apellidoPaterno],
  ([nombre, apellido]) => {
    if (modo.value == "nuevo" && !nombreEditado.value)
      formulario.nombreUsuario = NombrePropio([nombre, apellido].map((x) => x.trim()).filter(Boolean).join(" "));
  }
);

watch(
  () => formulario.colaborador.empresaId,
  () => {
    if (!areasFiltradas.value.some((a) => a.areaId == formulario.colaborador.areaId)) formulario.colaborador.areaId = "";
  }
);

/** Ciclos de vida  */
onMounted(async () => {
  await CargarDatos();
});
</script>
<template>
  <div class="container-fluid">
    <h2 class="text-secondary mb-3">Administración de usuarios</h2>

    <!-- Búsqueda y alta -->
    <section class="card mb-4">
      <div class="card-header bg-white">
        <h6 class="text-secondary mb-0">Alta o edición de usuario</h6>
      </div>
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label for="inputCorreo" class="form-label">Correo del colaborador</label>
            <input
              id="inputCorreo"
              type="email"
              class="form-control"
              placeholder="nombre@hircasa.com.mx"
              v-model="correo"
              :disabled="!!resultado"
              @keyup.enter="Buscar()"
            />
          </div>
          <div class="col-md-6 d-flex gap-2">
            <button
              v-if="!resultado"
              class="btn btn-primary text-white"
              type="button"
              :disabled="buscando || !correo.trim()"
              @click="Buscar()"
            >
              <i class="fas fa-search"></i> {{ buscando ? "Buscando en SRP y AuthHIR..." : "Buscar" }}
            </button>
            <button v-else class="btn btn-outline-secondary" type="button" @click="Limpiar()">
              <i class="fas fa-times"></i> Buscar otro correo
            </button>
          </div>
        </div>

        <template v-if="resultado">
          <!-- Estado de la búsqueda -->
          <div class="estado mt-3" :class="modo">
            <template v-if="modo == 'srp'">
              <i class="fas fa-user-check"></i>
              <div>
                <strong>{{ resultado.usuarioSRP.userName }} ya está registrado en SRP.</strong>
                <span>Puedes cambiar sus roles. Colaborador AuthHIR #{{ resultado.usuarioSRP.colaboradorId }}.</span>
              </div>
            </template>
            <template v-else-if="modo == 'authhir'">
              <i class="fas fa-id-badge"></i>
              <div>
                <strong>El correo existe en AuthHIR; se tomará como referencia.</strong>
                <span>
                  Colaborador #{{ resultado.colaboradorId }}
                  <template v-if="resultado.colaborador">
                    · {{ resultado.colaborador.nombreCompleto || resultado.colaborador.nombre }}
                    <template v-if="resultado.colaborador.puesto"> · {{ resultado.colaborador.puesto }}</template>
                  </template>
                  <template v-else> · No se pudieron leer sus datos, pero la cuenta existe.</template>
                </span>
              </div>
            </template>
            <template v-else>
              <i class="fas fa-user-plus"></i>
              <div>
                <strong>El correo no existe en AuthHIR.</strong>
                <span>
                  Captura los datos del colaborador: se creará en AuthHIR y después en SRP. AuthHIR no recibe
                  contraseña, así que la persona deberá definirla antes de iniciar sesión.
                </span>
              </div>
            </template>
          </div>

          <!-- Datos para AuthHIR -->
          <div v-if="modo == 'nuevo'" class="subformulario mt-3">
            <h6 class="text-secondary mb-0">Datos del colaborador para AuthHIR</h6>
            <p class="text-muted small mb-2">Se envían en mayúsculas, como los guarda HirCore. * Obligatorio.</p>
            <p class="text-danger small mb-2" v-if="catalogos.error">{{ catalogos.error }}</p>
            <div class="row g-2">
              <div class="col-md-4">
                <label for="colNombre" class="form-label">Nombre(s) *</label>
                <input id="colNombre" data-mayusculas type="text" class="form-control" maxlength="100" v-model="formulario.colaborador.nombre" />
              </div>
              <div class="col-md-4">
                <label for="colPaterno" class="form-label">Apellido paterno *</label>
                <input
                  id="colPaterno" data-mayusculas
                  type="text"
                  class="form-control"
                  maxlength="100"
                  v-model="formulario.colaborador.apellidoPaterno"
                />
              </div>
              <div class="col-md-4">
                <label for="colMaterno" class="form-label">Apellido materno *</label>
                <input
                  id="colMaterno" data-mayusculas
                  type="text"
                  class="form-control"
                  maxlength="100"
                  v-model="formulario.colaborador.apellidoMaterno"
                />
              </div>
              <div class="col-md-4">
                <label for="colEmpresa" class="form-label">Empresa *</label>
                <select id="colEmpresa" class="form-select" v-model="formulario.colaborador.empresaId">
                  <option value="" disabled>Selecciona una empresa</option>
                  <option v-for="e in catalogos.empresas" :key="e.empresaId" :value="e.empresaId">{{ e.nombre }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label for="colArea" class="form-label">Área *</label>
                <select id="colArea" class="form-select" v-model="formulario.colaborador.areaId">
                  <option value="" disabled>Selecciona un área</option>
                  <option v-for="a in areasFiltradas" :key="a.areaId" :value="a.areaId">{{ a.nombreOficial }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label for="colSucursal" class="form-label">Sucursal *</label>
                <select id="colSucursal" class="form-select" v-model="formulario.colaborador.sucursalId">
                  <option value="" disabled>Selecciona una sucursal</option>
                  <option v-for="s in catalogos.sucursales" :key="s.sucursalId" :value="s.sucursalId">{{ s.nombre }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label for="colPuesto" class="form-label">Puesto *</label>
                <input id="colPuesto" data-mayusculas type="text" class="form-control" maxlength="150" v-model="formulario.colaborador.puesto" />
              </div>
              <div class="col-md-4">
                <label for="colExtension" class="form-label">Extensión</label>
                <input
                  id="colExtension"
                  type="text"
                  class="form-control"
                  maxlength="20"
                  v-model="formulario.colaborador.extension"
                />
              </div>
              <div class="col-md-4">
                <label for="colClave" class="form-label">Nómina / Clave *</label>
                <input id="colClave" data-mayusculas type="text" class="form-control" maxlength="100" v-model="formulario.colaborador.clave" />
              </div>
            </div>
          </div>

          <!-- Nombre en SRP -->
          <div class="row g-2 mt-2" v-if="modo != 'srp'">
            <div class="col-md-6">
              <label for="inputNombreUsuario" class="form-label">Nombre que verá SRP *</label>
              <input
                id="inputNombreUsuario"
                type="text"
                class="form-control"
                maxlength="256"
                placeholder="Nombre y apellido"
                v-model="formulario.nombreUsuario"
                @input="nombreEditado = true"
              />
              <small class="text-muted">Se muestra en bandejas, correos e históricos.</small>
            </div>
          </div>

          <!-- Roles y revisión del flujo -->
          <div class="row g-3 mt-1">
            <div class="col-lg-6">
              <div class="d-flex justify-content-between align-items-end mb-2">
                <h6 class="text-secondary mb-0">Roles en SRP ({{ formulario.roles.length }})</h6>
                <div class="form-group buscador-roles">
                  <span class="fas fa-search form-control-icon"></span>
                  <input type="text" class="form-control" placeholder="Filtrar roles" v-model="filtroRoles" />
                </div>
              </div>
              <div class="lista-roles">
                <div v-for="grupo in gruposRoles" :key="grupo.clave" class="grupo-rol">
                  <div class="grupo-titulo" :class="grupo.clave">{{ grupo.titulo }}</div>
                  <label v-for="rol in grupo.roles" :key="rol.id" class="opcion-rol" :for="`rol-${rol.id}`">
                    <input
                      :id="`rol-${rol.id}`"
                      class="form-check-input"
                      type="checkbox"
                      :value="rol.id"
                      v-model="formulario.roles"
                    />
                    <span class="flex-grow-1">{{ rol.name }}</span>
                    <span class="text-muted small">{{ rol.usuarios }} pers.</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="col-lg-6">
              <h6 class="text-secondary mb-2">Flujo de autorización que tendrá</h6>
              <div class="revision">
                <p v-if="formulario.roles.length == 0" class="text-muted mb-0">
                  Selecciona uno o más roles para revisar a quién enviará, quién le enviará y con qué perfil entrará.
                </p>
                <template v-else>
                  <div class="mb-2">
                    <span class="text-muted small">Entrará al front como:</span>
                    <span v-for="p in perfilesSeleccion" :key="p" class="chip ms-1">{{ p }}</span>
                    <span v-if="perfilesSeleccion.length == 0" class="text-danger small ms-1">ningún perfil</span>
                  </div>
                  <div v-for="r in revisionFlujo" :key="r.id" class="rol-revision">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                      <strong>{{ r.nombre }}</strong>
                      <button class="btn btn-link btn-sm p-0 text-muted" type="button" title="Quitar rol" @click="QuitarRol(r.id)">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <ul v-if="r.lineas.length" class="mb-1">
                      <li v-for="(l, i) in r.lineas" :key="i" :class="{ 'text-danger': l.alerta }">
                        {{ l.texto }} <span class="text-muted">({{ l.detalle }})</span>
                      </li>
                    </ul>
                    <p v-for="(a, i) in r.avisos" :key="`a${i}`" class="aviso mb-1">
                      <i class="fas fa-exclamation-triangle"></i> {{ a }}
                    </p>
                    <p v-if="!r.lineas.length && !r.avisos.length" class="text-muted small mb-0">
                      Rol de flujo global: ve todas las requisiciones en su estatus.
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Grafo de roles: antes y después -->
          <div class="comparacion mt-3" v-if="rolesAntes.length || formulario.roles.length">
            <div class="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
              <h6 class="text-secondary mb-0">Grafo de roles de {{ formulario.nombreUsuario || resultado.email }}</h6>
              <div class="leyenda">
                <span><i class="caja agrega"></i>Gana</span>
                <span><i class="caja quita"></i>Pierde</span>
                <span><svg width="22" height="8" aria-hidden="true"><path d="M1 4h20" stroke="#b9b8d3" stroke-width="1.6" stroke-dasharray="5 4" /></svg> verificación</span>
                <span><svg width="22" height="8" aria-hidden="true"><path d="M1 4h20" stroke="#b9b8d3" stroke-width="1.6" /></svg> autorización</span>
              </div>
            </div>

            <div class="resumen-cambios mb-2">
              <template v-if="modo == 'srp' && cambios.sinCambios">
                <span class="text-muted">Sin cambios: conserva los mismos {{ rolesAntes.length }} roles.</span>
              </template>
              <template v-else>
                <span v-if="cambios.agregados.length" class="cambio agrega">
                  + {{ cambios.agregados.length }} rol(es): {{ cambios.agregados.map(NombreRol).join(", ") }}
                </span>
                <span v-if="cambios.quitados.length" class="cambio quita">
                  − {{ cambios.quitados.length }} rol(es): {{ cambios.quitados.map(NombreRol).join(", ") }}
                </span>
                <span v-if="cambios.perfilesGana.length" class="cambio agrega">
                  Nuevo acceso en el front: {{ cambios.perfilesGana.join(", ") }}
                </span>
                <span v-if="cambios.perfilesPierde.length" class="cambio quita">
                  Deja de ver: {{ cambios.perfilesPierde.join(", ") }}
                </span>
              </template>
            </div>

            <div class="grafos">
              <GrafoRolesUsuario
                :titulo="modo == 'srp' ? 'Antes (roles actuales en SRP)' : 'Antes (sin usuario en SRP)'"
                modo="antes"
                :roles-persona="rolesAntes"
                :otros-roles="formulario.roles"
                :roles="roles"
                :relaciones="relaciones"
                :perfil-front="PerfilFront"
              />
              <GrafoRolesUsuario
                titulo="Después (roles seleccionados)"
                modo="despues"
                :roles-persona="formulario.roles"
                :otros-roles="rolesAntes"
                :roles="roles"
                :relaciones="relaciones"
                :perfil-front="PerfilFront"
              />
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-outline-secondary" type="button" @click="Limpiar()">Cancelar</button>
            <button class="btn btn-primary text-white" type="button" :disabled="!puedeGuardar" @click="Guardar()">
              <i class="fas fa-save"></i>
              {{
                guardando
                  ? "Guardando..."
                  : modo == "srp"
                  ? "Guardar roles"
                  : modo == "nuevo"
                  ? "Crear en AuthHIR y SRP"
                  : "Dar de alta en SRP"
              }}
            </button>
          </div>
        </template>
      </div>
    </section>

    <!-- Usuarios de SRP -->
    <div class="card">
      <div class="card-header bg-white">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <div class="row">
                <div class="col-1">
                  <a class="btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fas fa-filter"></i>
                  </a>
                  <ul class="dropdown-menu">
                    <label class="list-group-item border-0"><strong> Filtro por: Perfil </strong></label>
                    <li v-for="p in perfilesDisponibles" :key="p" class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" :value="p" v-model="filtroPerfiles" />
                      <label>{{ p }}</label>
                    </li>
                  </ul>
                </div>
                <div class="col-10">
                  <span class="fas fa-search form-control-icon"></span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Busca por nombre, correo o rol"
                    v-model="buscador"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive tabla-scroll">
          <table class="table table-hover">
            <thead>
              <tr class="text-center">
                <th scope="col">
                  <span @click="OrdenarPor('userName')" :class="OrdenarClass('userName')">Nombre</span>
                </th>
                <th scope="col">
                  <span @click="OrdenarPor('email')" :class="OrdenarClass('email')">Correo</span>
                </th>
                <th scope="col"><span class="no-pointer">Roles</span></th>
                <th scope="col"><span class="no-pointer">Perfiles en el front</span></th>
                <th scope="col"><span class="no-pointer">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="5" class="text-center text-secondary">Cargando...</td>
              </tr>
              <tr v-else-if="usuariosFiltrados.length == 0">
                <td colspan="5" class="text-center text-secondary">No se encontraron usuarios.</td>
              </tr>
              <tr v-for="u in usuariosFiltrados" :key="u.id">
                <td class="text-nowrap">{{ u.userName }}</td>
                <td class="correo">{{ u.email }}</td>
                <td>
                  <span v-for="r in u.roles" :key="r" class="chip" :class="ClaseChip(r)" :title="`Rol ${r}`">
                    {{ NombreRol(r) }}
                  </span>
                  <span v-if="u.roles.length == 0" class="text-danger">Sin roles</span>
                </td>
                <td>
                  <span v-for="p in PerfilesUsuario(u)" :key="p" class="chip">{{ p }}</span>
                  <span v-if="u.roles.length && PerfilesUsuario(u).length == 0" class="text-danger">Ninguno</span>
                </td>
                <td class="text-center">
                  <span class="fas fa-user-edit btn" title="Editar roles" @click="EditarUsuario(u)"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer bg-white text-secondary text-end px-3">
        Mostrando {{ usuariosFiltrados.length }} de {{ usuarios.length }} usuarios
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "src/sass/bootstrap-variables.scss";

.container-fluid {
  margin-top: 15px;
}

.card {
  font-size: 12px;
}

.subformulario {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.estado {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;

  i {
    font-size: 20px;
    margin-top: 2px;
  }

  div {
    display: grid;
  }

  &.srp {
    background: #e6e5f4;
    color: $secondary;
  }

  &.authhir {
    background: #dff3e5;
    color: $success;
  }

  &.nuevo {
    background: #fff1d6;
    color: #8a5a00;
  }
}

.lista-roles {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.grupo-titulo {
  position: sticky;
  top: 0;
  background: #f7f7fb;
  padding: 6px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: $secondary;
  z-index: 1;

  &.ver {
    color: #0f8f8a;
  }

  &.aut {
    color: #c77700;
  }
}

.opcion-rol {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 5px 10px;
  cursor: pointer;

  &:hover {
    background: #fafafa;
  }
}

.revision {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  max-height: 400px;
  overflow-y: auto;
}

.rol-revision {
  border-top: 1px solid #eee;
  padding: 8px 0;

  ul {
    padding-left: 18px;
  }
}

.aviso {
  color: #c2410c;
  background: #fde7dc;
  border-radius: 6px;
  padding: 4px 8px;
}

.chip {
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #eeeef6;
  color: #5b5a7a;
  margin: 1px 3px 1px 0;
  white-space: nowrap;

  &.s {
    color: $secondary;
  }

  &.v {
    color: #0f8f8a;
    background: #dcf3f1;
  }

  &.a {
    color: #c77700;
    background: #fff1d6;
  }
}

[data-mayusculas] {
  text-transform: uppercase;
}

.comparacion {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.grafos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 12px;
}

.leyenda {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11.5px;
  color: #6f6f6f;

  .caja {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    margin-right: 5px;
    vertical-align: -2px;
    border: 1px solid;

    &.agrega {
      background: #e3f4ea;
      border-color: $success;
    }

    &.quita {
      background: #fbe4e6;
      border-color: $danger;
    }
  }
}

.resumen-cambios {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .cambio {
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 6px;

    &.agrega {
      background: #e3f4ea;
      color: $success;
    }

    &.quita {
      background: #fbe4e6;
      color: $danger;
    }
  }
}

td.correo {
  color: #6f6f6f;
}

/* Encabezados ordenables, igual que en los históricos de requisiciones */
.sorted.asc::after {
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f885";
  margin-left: 3px;
}

.sorted.desc::after {
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f160";
  margin-left: 3px;
}

.sorted.asc.active,
.sorted.desc.active {
  color: $primary !important;
}

th {
  vertical-align: middle;
}

th span.no-pointer {
  cursor: default !important;
}

th span {
  color: $secondary;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  user-select: none;
}

td {
  vertical-align: middle;
}

.dropdown-toggle {
  &::after {
    display: none !important;
  }
}

.form-group {
  position: relative;

  .form-control {
    padding-left: 2.375rem;
    background-color: #f2f2f2;
    color: #828282;
    border: 0px;
  }

  .form-control-icon {
    position: absolute;
    z-index: 2;
    display: block;
    width: 2rem;
    height: 2rem;
    line-height: 2rem;
    text-align: center;
    pointer-events: none;
    color: #aaa;
  }
}

.buscador-roles {
  width: 220px;
}

.tabla-scroll {
  max-height: 520px;
  overflow-y: auto;

  thead th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 1;
  }
}
</style>
